import React from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox, Spinner } from '@kui/foundations-react'
import ConfigManager from '../../config/configManager'
import * as ProductFamilies from "../../constants/productFamilies";

const isAllowedDGXFamily = (productFamily) => {
  if (!productFamily) return false;
  return ProductFamilies.ALLOWED_DGX_FAMILIES.has(productFamily.toUpperCase());
};

// Partners whose trials use a "contact me" consent checkbox instead of the NVIDIA terms line
const PARTNER_CONSENT = [
  { id: ProductFamilies.PartnerHP, name: 'HP', policy: 'HPPrivacyPolicy' },
  { id: ProductFamilies.PartnerWWT, name: 'WWT', policy: 'WWTPrivacyPolicy' },
  { id: ProductFamilies.PartnerESCAPE, name: 'Escape', policy: 'EscapePrivacyPolicy' },
  { id: ProductFamilies.PartnerBoxx, name: 'Boxx', policy: 'BoxxPrivacyPolicy' },
  { id: ProductFamilies.PartnerLenovo, name: 'Lenovo', policy: 'LenovoPrivacyPolicy' },
  { id: ProductFamilies.PartnerINSIGHT, name: 'Insight', policy: 'InsightPrivacyPolicy' },
];

const ExternalLink = ({ href, children }) => (
  <a href={href} target='_blank' rel='noopener noreferrer'>
    {children}
  </a>
);

ExternalLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
};

const RequiredMark = () => (
  <span className='regRequiredMark' aria-hidden='true'>
    {' '}*
  </span>
);

// App handlers read e.target.checked, so adapt the @kui callback to that shape
const toChangeHandler = (handler) => (checked) => {
  if (handler) handler({ target: { checked: checked === true } });
};

const ActionBar = props => {
  const Config = ConfigManager();
  const showDGXTerms = isAllowedDGXFamily(props.productFamily);
  const submitDisabled =
    props.submitting ||
    !props.allowSubmit ||
    (props.registerTermsRequired && !props.agreeToRegisterTerms);

  const partner = props.partnerValue ? props.partnerValue.toUpperCase() : '';
  const consent = PARTNER_CONSENT.find(p => p.id === partner);
  const isAnyScale = partner === ProductFamilies.PartnerAnyScale;

  let newsText = 'Send me the latest enterprise news, announcements, and more from NVIDIA. I can unsubscribe at any time.';
  if (consent) {
    newsText = `Thank you for your interest in Omniverse Enterprise. As part of the trial, I understand that I will be contacted by ${consent.name} to learn more about my experience.`;
  } else if (isAnyScale) {
    newsText = 'Thank you for your interest in NVIDIA AI Enterprise. Yes, I would like to hear from Anyscale for a preview of the NVIDIA AI Enterprise integration on Anyscale Platform.';
  }

  let termsLabel = null;
  if (isAnyScale) {
    termsLabel = (
      <>
        By clicking Register you agree to{' '}
        <ExternalLink href={Config.TermsLink}>NVIDIA Account Terms and Conditions</ExternalLink>
        {' '}, &amp;{' '}
        <ExternalLink href={Config.AnyScaleTerms}>Anyscale Terms</ExternalLink>
      </>
    );
  } else if (!consent) {
    termsLabel = (
      <>
        By clicking Register you agree to{' '}
        <ExternalLink href={Config.TermsLink}>NVIDIA Account Terms and Conditions</ExternalLink>
        {' '}, <ExternalLink href={Config.LegalInfo}>{props.IsRWA02 ? 'Terms of Service' : 'Legal Info'}</ExternalLink>
        {' '}&amp;{' '}
        <ExternalLink href={Config.PrivacyPolicy}>Privacy Policy</ExternalLink>
      </>
    );
  }

  const showDgxTermsCheckbox = !consent && !isAnyScale && props.productFamily && showDGXTerms;

  // mode: "single"      one-screen form: consents + Register
  //       "details"     first step of a two-step form: Continue only
  //       "environment" second step: consents + Back + Register
  const mode = props.mode || 'single';

  // verifyMode (returning user, "Verify Enterprise Account Information"):
  //   "edit"   Cancel + Save only
  //   "verify" the normal buttons of the mode, with Edit next to them
  const verifyMode = props.verifyMode;
  const editButton =
    verifyMode === 'verify' ? (
      <Button type='button' color='neutral' kind='tertiary' onClick={props.handleVerifyEdit}>
        Edit
      </Button>
    ) : null;

  if (verifyMode === 'edit') {
    return (
      <div className='regActions'>
        <div className='regButtons regButtons--end'>
          <Button type='button' color='neutral' kind='tertiary' onClick={props.handleVerifyCancel}>
            Cancel
          </Button>
          <Button type='button' color='brand' kind='primary' onClick={props.handleVerifySave}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='regActions'>
      {mode !== 'details' && (
        <>
          <Checkbox
            checked={props.sendNews}
            onCheckedChange={toChangeHandler(props.handleSendNewsChange)}
            slotLabel={newsText}
          />
          {consent && (
            <div>
              <ExternalLink href={Config[consent.policy]}>
                View {consent.name}’s privacy policy.
              </ExternalLink>
            </div>
          )}
          {termsLabel && (
            <Checkbox
              checked={props.agreeToRegisterTerms}
              onCheckedChange={toChangeHandler(props.handleRegisterTermsChange)}
              slotLabel={<>{termsLabel}{props.registerTermsRequired && <RequiredMark />}</>}
            />
          )}
          {showDgxTermsCheckbox && (
            <Checkbox
              checked={props.agreeToDgxTerms}
              onCheckedChange={toChangeHandler(props.handleDgxTermsChange)}
              slotLabel={
                <>
                  <ExternalLink href={Config.DGXTermsLink}>
                    I agree to DGX Terms and Conditions.
                  </ExternalLink>
                  <RequiredMark />
                </>
              }
            />
          )}
        </>
      )}
      {mode === 'details' ? (
        verifyMode === 'verify' ? (
          <div className='regButtons regButtons--end'>
            {editButton}
            <Button
              type='button'
              color='brand'
              kind='primary'
              onClick={props.handleContinueClick}
              disabled={!props.allowSubmit}
            >
              Continue
            </Button>
          </div>
        ) : (
          <Button
            type='button'
            color='brand'
            kind='primary'
            onClick={props.handleContinueClick}
            disabled={!props.allowSubmit}
            style={{ width: '100%' }}
          >
            Continue
          </Button>
        )
      ) : (
        <div className='regButtons'>
          {editButton}
          {mode === 'environment' && (
            <Button
              type='button'
              color='neutral'
              kind='tertiary'
              onClick={props.handleBackClick}
            >
              Back
            </Button>
          )}
          <Button
            type='submit'
            color='brand'
            kind='primary'
            onClick={props.handleSubmitClick}
            disabled={submitDisabled}
            style={{ flex: 1 }}
          >
            {props.submitting ? (
              <>
                <span className='regBtnSpinner'>
                  <Spinner size='small' aria-label='Submitting registration' />
                </span>
                Registering...
              </>
            ) : (
              'Register'
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

ActionBar.propTypes = {
  IsRWA02: PropTypes.bool,
  mode: PropTypes.oneOf(['single', 'details', 'environment']),
  verifyMode: PropTypes.oneOf(['verify', 'edit']),
  handleVerifyEdit: PropTypes.func,
  handleVerifyCancel: PropTypes.func,
  handleVerifySave: PropTypes.func,
  handleContinueClick: PropTypes.func,
  handleBackClick: PropTypes.func,
  partnerValue: PropTypes.string,
  productFamily: PropTypes.string,
  sendNews: PropTypes.bool.isRequired,
  handleSendNewsChange: PropTypes.func.isRequired,
  handleSubmitClick: PropTypes.func.isRequired,
  allowSubmit: PropTypes.bool.isRequired,
  submitting: PropTypes.bool,
  registerTermsRequired: PropTypes.bool,
  agreeToRegisterTerms: PropTypes.bool.isRequired,
  handleRegisterTermsChange: PropTypes.func.isRequired,
  agreeToDgxTerms: PropTypes.bool,
  handleDgxTermsChange: PropTypes.func,
}

export default ActionBar
